import React, { useContext, useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { UserContext } from "@src/context/user-context"
import FormContext from "./forms/form-context"
import FormReview from "./forms/form-review"
import Link from "@src/components/core-link"
import IconDetailContentTile from "@src/components/core-icon-detail-tile"

const Modal = styled.div`
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  position: fixed;
  z-index: 999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.4);
  justify-content: center;
  align-items: center;

  @media only screen and (${props => props.theme.screen.small.min}) {
    height: 100%;
  }
`

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: auto;
  padding: 56px 56px 32px;
  border: 1px solid #888;
  border-radius: 16px;
  background: #fff;
  width: 100vw;
  height: max-content;
  min-height: max-content;
  flex-shrink: 0;

  p {
    margin-top: 48px;
    text-align: center;
  }

  .header {
    color: #315254;
    text-align: center;
    font-feature-settings:
      "clig" off,
      "liga" off;

    /* Headings/Heading3 */
    font-family: system-ui;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 44px; /* 137.5% */
    letter-spacing: 0.5px;
  }
  @media only screen and (${props => props.theme.screen.small.min}) {
    width: 528px;
    height: 400px;
  }
`
const CloseButton = styled.span`
  float: right;
  color: #1f1f23;
  font-size: 40px;
  cursor: pointer;
  display: flex;
  width: 13.414px;
  height: 13.414px;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  &:hover,
  &:focus {
    color: black;
    text-decoration: none;
    cursor: pointer;
  }
`

const WriteReviewButton = styled.div`
  display: inline-flex;
  width: -webkit-fill-available;
  height: 50px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #1f1f23;
  margin-top: 24px;

  /* Shadow/xs */
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  color: #fff;
  font-family: system-ui;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 133.333% */
  letter-spacing: 0.09px;

  @media only screen and (${props => props.theme.screen.small.min}) {
    width: 184px;
    height: 50px;
    float: right;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    border-radius: 8px;
    background: #1f1f23;
    cursor: pointer;
    margin-bottom: 50px;
  }
`

const ReviewHeaderDiv = styled.div`
  height: 100%;
  width: 100%;
  margin-bottom: 24px;
  @media only screen and (${props => props.theme.screen.small.min}) {
    height: 100px;
    margin-bottom: 10px;
  }
`

const RatingDiv = styled.div`
  margin-bottom: 24px;
  width: 50%;
  height: 86px;
  float: left;
  @media only screen and (${props => props.theme.screen.small.min}) {
    width: 50%;
    height: 86px;
    float: left;
  }
`

const StyledStar = styled(IconDetailContentTile)`
  display: inline-flex;
  margin-top: 16px;
  width: 34px;
  height: 32px;
  float: left;
  & .icon {
    font-size: 34px;
  }
`

const AverageRating = styled.div`
  display: inline-flex;
  margin-left: 10px;
  width: 77px;
  height: 64px;
  color: #272d37;
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: system-ui;
  font-size: 46px;
  font-style: normal;
  font-weight: 900;
  line-height: 64px; /* 139.13% */
  letter-spacing: 0.5px;
`
const TotalReviews = styled.div`
  margin-left: 44px;
  color: #4f4f4f;
  font-family: system-ui;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 22.4px */
  letter-spacing: 0.32px;
`

const AddRouteGuideReviewModal = ({
  routeId,
  onReviewAdded,
  overallRating,
  totalReviews,
}) => {
  const { user } = useContext(UserContext)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <ReviewHeaderDiv>
        <RatingDiv>
          <StyledStar glyph="star" color="primary" size="medium" />
          <AverageRating>{overallRating}</AverageRating>
          {totalReviews !== 0 ? (
            <TotalReviews>{totalReviews} reviews</TotalReviews>
          ) : (
            <TotalReviews>No reviews</TotalReviews>
          )}
        </RatingDiv>
        <WriteReviewButton onClick={openModal}>
          Write a review
        </WriteReviewButton>
      </ReviewHeaderDiv>
      <Modal $isOpen={isModalOpen}>
        <ModalContent>
          <CloseButton onClick={closeModal}>&times;</CloseButton>
          <h3 className="header">Add a review</h3>
          {user ? (
            <FormContext>
              <FormReview routeId={routeId} onReviewAdded={onReviewAdded} />
            </FormContext>
          ) : (
            <p>
              You must be <Link to="/account/login/">logged in</Link> to post a
              comment.
            </p>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
AddRouteGuideReviewModal.propTypes = {
  routeId: PropTypes.number,
  onReviewAdded: PropTypes.func,
  overallRating: PropTypes.string,
  totalReviews: PropTypes.number,
}
export default AddRouteGuideReviewModal
